import React, { useState } from 'react';
import { Detail, InboundLine, ScanReply } from './inboundApi';

// Fixing a product's barcode mid-inbound (Fernando 30.10: a 3XT came with a barcode the order line did not carry;
// his fix in the Products page did not reach the inbound). The editor: the line's product, its current barcode, the
// new one (typed, or scanned straight into the field), "keep the old one too" when the goods still carry it. Save
// goes through the server's gate (products_edit, Tier 4 by default): a person with the right saves; anyone else is
// asked for a manager's PIN or badge by the approval box; their own badge is refused there. The LinePicker is the
// strip's "It IS on the order": the inbound's lines, this product's style first. -- Claude, 2026-10-30

const field = 'bg-white border border-[#9aa0a6] text-[#111111] rounded px-2 py-1.5 text-base';

export function BarcodeEditor({ line, initial, onSave, onClose }: {
  line: InboundLine; initial?: string;
  /** resolves with an error text to show, or null when saved */
  onSave: (barcode: string, keepOld: boolean) => Promise<string | null>;
  onClose: () => void;
}) {
  const [code, setCode] = useState(initial || '');
  const [keep, setKeep] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const clean = code.replace(/\s+/g, '');
  const valid = /^\d{8,14}$/.test(clean);
  const save = async () => {
    if (!valid) { setErr('A barcode is 8–14 digits'); return; }
    setBusy(true); setErr('');
    const e = await onSave(clean, keep);
    setBusy(false);
    if (e) setErr(e); else onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white text-[#111111] rounded-2xl border border-[#9aa0a6] shadow-xl w-full max-w-lg p-5" onClick={e => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-wider text-[#5f6368]">Fix the barcode</div>
        <div className="text-lg font-semibold mt-1">{line.name || line.article_number} <span className="font-bold">{line.size}</span></div>
        <div className="text-sm text-[#5f6368]">{line.article_number} · current barcode <span className="font-mono text-[#111111]">{line.ean || '— none —'}</span></div>
        <label className="block mt-4 text-sm font-semibold">New barcode
          <input autoFocus value={code} onChange={e => setCode(e.target.value)} placeholder="scan the piece's barcode, or type it"
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onClose(); }}
            className={`${field} w-full mt-1 font-mono tracking-wider ${code && !valid ? 'border-red-500' : ''}`} />
        </label>
        {line.ean && line.ean !== clean && (
          <label className="flex items-start gap-2 mt-3 text-sm">
            <input type="checkbox" checked={keep} onChange={e => setKeep(e.target.checked)} className="mt-1" />
            <span>The old barcode <span className="font-mono">{line.ean}</span> is also on the goods — keep it as a second barcode
              <span className="block text-xs text-[#5f6368]">Unticked = the old barcode was wrong and is dropped. WGR gets the new one either way.</span></span>
          </label>
        )}
        {err && <div className="mt-3 text-sm text-red-700 font-semibold">{err}</div>}
        <div className="flex items-center gap-2 mt-4">
          <button disabled={busy || !valid} onClick={save} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-semibold">{busy ? 'Saving…' : 'Save barcode'}</button>
          <button onClick={onClose} className="px-3 py-2 rounded-lg bg-[#dfe3e8] hover:bg-[#d0d5db] text-sm">Cancel</button>
          <span className="ml-auto text-xs text-[#5f6368]">needs the right to edit products (Tier 4), or a manager's PIN / badge</span>
        </div>
      </div>
    </div>
  );
}

export function LinePicker({ d, last, onPick, onClose }: { d: Detail; last: ScanReply; onPick: (l: InboundLine) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const ordered = d.lines.filter(l => l.kind === 'ordered');
  const same = ordered.filter(l => last.style_key && l.style_key === last.style_key);
  const rest = ordered.filter(l => !same.includes(l));
  const hit = (l: InboundLine) => !q || `${l.article_number} ${l.name} ${l.size} ${l.ean}`.toLowerCase().includes(q.toLowerCase());
  const Row = ({ l }: { l: InboundLine }) => (
    <button onClick={() => onPick(l)} className="w-full text-left grid grid-cols-[3rem_1fr_5rem_5rem] gap-2 items-center px-3 py-2 border-t border-[#e5e7eb] hover:bg-blue-50">
      <span className="font-bold">{l.box_label ?? ''}</span>
      <span className="truncate"><span className="text-[#5f6368]">{l.article_number}</span> · {l.name}</span>
      <span className="font-semibold">{l.size}</span>
      <span className={`text-right ${l.total_scanned >= l.qty_ordered ? 'text-green-700' : ''}`}>{l.total_scanned} of {l.qty_ordered}</span>
    </button>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white text-[#111111] rounded-2xl border border-[#9aa0a6] shadow-xl w-full max-w-2xl p-5 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-wider text-[#5f6368]">This piece IS on the order</div>
        <div className="text-lg font-semibold mt-1">Which line does <span className="font-mono">{last.barcode}</span> belong to?</div>
        <div className="text-sm text-[#5f6368]">The line's product gets this barcode; the piece is counted there.</div>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="filter: article, name, size…" className={`${field} w-full mt-3 text-sm`} />
        <div className="mt-3 overflow-y-auto min-h-0 border border-[#e5e7eb] rounded-lg">
          {same.filter(hit).length > 0 && <div className="px-3 py-1 text-xs uppercase tracking-wider text-[#5f6368] bg-[#f3f4f6]">Same product</div>}
          {same.filter(hit).map(l => <Row key={l.id} l={l} />)}
          {same.filter(hit).length > 0 && rest.filter(hit).length > 0 && <div className="px-3 py-1 text-xs uppercase tracking-wider text-[#5f6368] bg-[#f3f4f6]">Other lines</div>}
          {rest.filter(hit).map(l => <Row key={l.id} l={l} />)}
          {ordered.filter(hit).length === 0 && <div className="px-3 py-3 text-sm text-[#5f6368]">No line matches.</div>}
        </div>
        <div className="flex mt-3"><button onClick={onClose} className="px-3 py-2 rounded-lg bg-[#dfe3e8] hover:bg-[#d0d5db] text-sm">Cancel — it really is not on the order</button></div>
      </div>
    </div>
  );
}
